/* eslint-disable react/no-unknown-property */
import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  OrbitControls,
  Html,
  AdaptiveDpr,
  AdaptiveEvents,
  Stars,
} from "@react-three/drei";
import * as THREE from "three";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Play,
  Pause,
  Maximize2,
  Minimize2,
  RotateCw,
  PlayCircle,
  StopCircle,
  Loader2,
  Volume2,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useMedia } from "@/lib/SiteContent";
import { cn } from "@/lib/utils";
import {
  BOUNDARY_PDF,
  HOTSPOTS,
  MOUNTAIN_RIDGES_PDF,
  PLOT_CLUSTERS_PDF,
  ROADS_PDF,
  SCALE,
  pdfToWorld,
} from "./silvaCityLayout";
import ProjectMinimap from "./ProjectMinimap";
import {
  M,
  Clubhouse,
  Temple,
  Playground,
  Amphitheater,
  SocialCentre,
  WellnessCentre,
  EntranceGate,
  PlotHouse,
  Tree,
  ParkPavilion,
  FloatingMarker,
} from "./buildings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const audioUrlFor = (key) => (key ? `${API_BASE}/audio/${key}` : null);

// =============================================================
// SHAPE / GEOMETRY HELPERS
// =============================================================

const buildSmoothShape = (pdfPoints, smoothness = 8) => {
  const v3 = pdfPoints.map(([x, y]) => {
    const [wx, wz] = pdfToWorld([x, y]);
    return new THREE.Vector3(wx, 0, wz);
  });
  const curve = new THREE.CatmullRomCurve3(v3, true, "catmullrom", 0.4);
  const samples = curve.getPoints(pdfPoints.length * smoothness);
  const shape = new THREE.Shape();
  shape.moveTo(samples[0].x, samples[0].z);
  for (let i = 1; i < samples.length; i++) {
    shape.lineTo(samples[i].x, samples[i].z);
  }
  shape.closePath();
  return shape;
};

// =============================================================
// SITE PLATE — large irregular green polygon
// =============================================================

const SitePlate = () => {
  const { geometry, rimGeometry, accentRimGeometry } = useMemo(() => {
    const shape = buildSmoothShape(BOUNDARY_PDF, 10);
    const g = new THREE.ExtrudeGeometry(shape, {
      depth: 0.5,
      bevelEnabled: true,
      bevelThickness: 0.1,
      bevelSize: 0.15,
      bevelSegments: 4,
      curveSegments: 48,
    });
    g.rotateX(-Math.PI / 2);

    const v3 = BOUNDARY_PDF.map(([x, y]) => {
      const [wx, wz] = pdfToWorld([x, y]);
      return new THREE.Vector3(wx, 0, wz);
    });
    const c = new THREE.CatmullRomCurve3(v3, true, "catmullrom", 0.4);
    const rimPoints = c.getPoints(BOUNDARY_PDF.length * 12);
    const rim = new THREE.BufferGeometry().setFromPoints(rimPoints);
    return { geometry: g, rimGeometry: rim, accentRimGeometry: rim.clone() };
  }, []);

  return (
    <group>
      {/* Outer void / dark base */}
      <mesh position={[0, -0.6, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[36, 64]} />
        <meshStandardMaterial color="#050707" roughness={1} metalness={0} />
      </mesh>
      {/* The land plate (extruded) — base color */}
      <mesh
        receiveShadow
        geometry={geometry}
        position={[0, -0.05, 0]}
      >
        <meshStandardMaterial
          color={M.grass}
          roughness={0.92}
          metalness={0.05}
        />
      </mesh>
      {/* Top grass layer (slightly above the plate, brighter green) */}
      <mesh
        position={[0, 0.456, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
      >
        <shapeGeometry args={[buildSmoothShape(BOUNDARY_PDF, 10)]} />
        <meshStandardMaterial
          color={M.grassMid}
          roughness={0.85}
          metalness={0.04}
        />
      </mesh>
      {/* Gold rim */}
      <lineLoop geometry={rimGeometry} position={[0, 0.47, 0]}>
        <lineBasicMaterial
          color={M.gold}
          transparent
          opacity={0.95}
        />
      </lineLoop>
      <lineLoop geometry={accentRimGeometry} position={[0, 0.48, 0]}>
        <lineBasicMaterial
          color={M.goldBright}
          transparent
          opacity={0.5}
        />
      </lineLoop>
    </group>
  );
};

// =============================================================
// MOUNTAINS — low-poly emerald ridges
// =============================================================

const Mountain = ({ pos, height, radius, seed = 0 }) => {
  const geom = useMemo(() => {
    const g = new THREE.ConeGeometry(radius, height, 7, 1, false);
    const pa = g.attributes.position;
    for (let i = 0; i < pa.count; i++) {
      const x = pa.getX(i);
      const y = pa.getY(i);
      const z = pa.getZ(i);
      if (y > height / 2 - 0.01) continue;
      const wob =
        Math.sin(i * 1.91 + seed) * 0.2 +
        Math.cos(i * 0.71 + seed * 2) * 0.13;
      pa.setX(i, x * (1 + wob * 0.28));
      pa.setZ(i, z * (1 + wob * 0.28));
    }
    g.computeVertexNormals();
    return g;
  }, [radius, height, seed]);

  return (
    <mesh
      position={pos}
      castShadow
      receiveShadow
      geometry={geom}
    >
      <meshStandardMaterial
        color="#0c1f15"
        roughness={0.92}
        metalness={0.05}
        flatShading
        emissive={M.emerald}
        emissiveIntensity={0.06}
      />
    </mesh>
  );
};

const MountainRange = () => {
  const peaks = useMemo(() => {
    const out = [];
    MOUNTAIN_RIDGES_PDF.forEach((ridge, ridgeIdx) => {
      for (let i = 0; i < ridge.length - 1; i++) {
        const [ax, ay] = ridge[i];
        const [bx, by] = ridge[i + 1];
        const steps = 4;
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const px = ax + (bx - ax) * t;
          const py = ay + (by - ay) * t;
          const dx = px - 50;
          const dy = py - 50;
          const dist = Math.hypot(dx, dy) || 1;
          const pushX = (dx / dist) * 2.5;
          const pushY = (dy / dist) * 2.5;
          const [wx, wz] = pdfToWorld([px + pushX, py + pushY]);
          const seed = ridgeIdx * 100 + i * 10 + s;
          const h = 5.0 + ((seed * 1.93) % 3.6);
          const r = 2.6 + ((seed * 0.71) % 1.8);
          out.push({ pos: [wx, h / 2, wz], height: h, radius: r, seed });
        }
      }
    });
    return out;
  }, []);

  return (
    <group>
      {peaks.map((p) => (
        <Mountain
          key={`peak-${p.seed}`}
          pos={p.pos}
          height={p.height}
          radius={p.radius}
          seed={p.seed}
        />
      ))}
    </group>
  );
};

// =============================================================
// ROADS — properly textured asphalt with dashed centerlines
// =============================================================

const Road = ({ points, width, accent = "dashed" }) => {
  const { ribbon, centerLineGeom, edgeLeftGeom, edgeRightGeom, segPts } =
    useMemo(() => {
      const v3 = points.map(([x, y]) => {
        const [wx, wz] = pdfToWorld([x, y]);
        return new THREE.Vector3(wx, 0, wz);
      });
      const curve = new THREE.CatmullRomCurve3(v3, false, "catmullrom", 0.35);
      const segs = curve.getPoints(points.length * 16);

      // Build flat ribbon
      const halfW = (width * SCALE) / 2;
      const positions = [];
      const indices = [];
      const leftEdge = [];
      const rightEdge = [];

      for (let i = 0; i < segs.length; i++) {
        const p = segs[i];
        let dir;
        if (i === segs.length - 1) {
          dir = segs[i].clone().sub(segs[i - 1]);
        } else {
          dir = segs[i + 1].clone().sub(segs[i]);
        }
        dir.y = 0;
        dir.normalize();
        const normal = new THREE.Vector3(-dir.z, 0, dir.x);
        const left = p.clone().addScaledVector(normal, halfW);
        const right = p.clone().addScaledVector(normal, -halfW);
        positions.push(left.x, 0.04, left.z, right.x, 0.04, right.z);
        leftEdge.push(new THREE.Vector3(left.x, 0.052, left.z));
        rightEdge.push(new THREE.Vector3(right.x, 0.052, right.z));

        if (i < segs.length - 1) {
          const base = i * 2;
          indices.push(base, base + 1, base + 2);
          indices.push(base + 1, base + 3, base + 2);
        }
      }
      const ribbonGeo = new THREE.BufferGeometry();
      ribbonGeo.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3)
      );
      ribbonGeo.setIndex(indices);
      ribbonGeo.computeVertexNormals();

      const centerLineGeo = new THREE.BufferGeometry().setFromPoints(segs);
      const edgeLeftGeo = new THREE.BufferGeometry().setFromPoints(leftEdge);
      const edgeRightGeo = new THREE.BufferGeometry().setFromPoints(rightEdge);
      return {
        ribbon: ribbonGeo,
        centerLineGeom: centerLineGeo,
        edgeLeftGeom: edgeLeftGeo,
        edgeRightGeom: edgeRightGeo,
        segPts: segs,
      };
    }, [points, width]);

  const isGold = accent === "gold";

  return (
    <group>
      {/* Asphalt ribbon */}
      <mesh geometry={ribbon} receiveShadow>
        <meshStandardMaterial
          color={isGold ? "#23231f" : M.road}
          roughness={0.92}
          metalness={0.12}
        />
      </mesh>
      {/* Centerline */}
      {isGold ? (
        // Premium spine — solid gold line
        <line geometry={centerLineGeom} position={[0, 0.056, 0]}>
          <lineBasicMaterial color={M.gold} transparent opacity={0.85} />
        </line>
      ) : (
        // Standard internal — dashed white markers
        <DashedCenterline points={segPts} />
      )}
      {/* White curb edges */}
      <line geometry={edgeLeftGeom}>
        <lineBasicMaterial
          color={isGold ? M.gold : M.roadMarkWhite}
          transparent
          opacity={isGold ? 0.45 : 0.55}
        />
      </line>
      <line geometry={edgeRightGeom}>
        <lineBasicMaterial
          color={isGold ? M.gold : M.roadMarkWhite}
          transparent
          opacity={isGold ? 0.45 : 0.55}
        />
      </line>
    </group>
  );
};

// Dashed center line drawn as small white rectangles
const DashedCenterline = ({ points }) => {
  const segments = useMemo(() => {
    // Sample evenly along the polyline
    const out = [];
    let cum = 0;
    const segLens = [];
    for (let i = 0; i < points.length - 1; i++) {
      segLens.push(points[i].distanceTo(points[i + 1]));
      cum += segLens[i];
    }
    const dashLen = 0.35;
    const gapLen = 0.32;
    const stride = dashLen + gapLen;
    const count = Math.floor(cum / stride);
    let target = 0;
    for (let k = 0; k < count; k++) {
      const start = k * stride;
      const mid = start + dashLen / 2;
      // Find which segment "mid" falls in
      let acc = 0;
      let segIdx = 0;
      while (segIdx < segLens.length && acc + segLens[segIdx] < mid) {
        acc += segLens[segIdx];
        segIdx++;
      }
      if (segIdx >= segLens.length) break;
      const segT = (mid - acc) / segLens[segIdx];
      const p0 = points[segIdx];
      const p1 = points[segIdx + 1];
      const px = p0.x + (p1.x - p0.x) * segT;
      const pz = p0.z + (p1.z - p0.z) * segT;
      const dir = new THREE.Vector2(p1.x - p0.x, p1.z - p0.z).normalize();
      const angle = Math.atan2(dir.y, dir.x);
      out.push({ x: px, z: pz, angle });
      target = k;
    }
    return out;
  }, [points]);

  return (
    <group>
      {segments.map((s) => (
        <mesh
          key={`dash-${s.x.toFixed(3)}-${s.z.toFixed(3)}`}
          position={[s.x, 0.055, s.z]}
          rotation={[-Math.PI / 2, 0, -s.angle]}
        >
          <planeGeometry args={[0.32, 0.06]} />
          <meshBasicMaterial
            color={M.roadMarkWhite}
            transparent
            opacity={0.85}
          />
        </mesh>
      ))}
    </group>
  );
};

const RoadNetwork = () => (
  <group>
    {ROADS_PDF.map((r) => (
      <Road
        key={r.id}
        points={r.points}
        width={r.width}
        accent={r.accent}
      />
    ))}
  </group>
);

// =============================================================
// PLOT CLUSTERS — green grass + small houses
// =============================================================

const PlotCluster = ({ corners, cols, rows, id }) => {
  const [x1pdf, y1pdf, x2pdf, y2pdf] = corners;
  const [wx1, wz1] = pdfToWorld([x1pdf, y1pdf]);
  const [wx2, wz2] = pdfToWorld([x2pdf, y2pdf]);
  const cx = (wx1 + wx2) / 2;
  const cz = (wz1 + wz2) / 2;
  const w = Math.abs(wx2 - wx1);
  const d = Math.abs(wz2 - wz1);
  const cellW = w / cols;
  const cellD = d / rows;

  const houses = useMemo(() => {
    const arr = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = wx1 + cellW * c + cellW / 2;
        const pz = wz1 + cellD * r + cellD / 2;
        arr.push({
          pos: [px, 0.47, pz],
          seed: r * cols + c + id.length,
          // Alternate house orientations
          rot: ((r + c) % 2) * (Math.PI / 2),
        });
      }
    }
    return arr;
  }, [rows, cols, cellW, cellD, wx1, wz1, id]);

  return (
    <group position={[0, 0, 0]}>
      {/* Bright green lawn pad */}
      <mesh
        position={[cx, 0.461, cz]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
      >
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial
          color={M.grassBright}
          roughness={0.85}
          metalness={0.04}
        />
      </mesh>
      {/* Plot dividers (subtle white grid) */}
      {Array.from({ length: cols + 1 }).map((_, i) => {
        const x = wx1 + cellW * i;
        return (
          <mesh
            key={`v-${id}-${i}`}
            position={[x, 0.466, cz]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <planeGeometry args={[0.02, d]} />
            <meshBasicMaterial
              color={M.cream}
              transparent
              opacity={0.25}
            />
          </mesh>
        );
      })}
      {Array.from({ length: rows + 1 }).map((_, i) => {
        const z = wz1 + cellD * i;
        return (
          <mesh
            key={`h-${id}-${i}`}
            position={[cx, 0.466, z]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <planeGeometry args={[w, 0.02]} />
            <meshBasicMaterial
              color={M.cream}
              transparent
              opacity={0.25}
            />
          </mesh>
        );
      })}
      {/* Houses */}
      {houses.map((h, i) => (
        <PlotHouse
          key={`${id}-${i}`}
          position={h.pos}
          rotation={[0, h.rot, 0]}
          seed={h.seed}
        />
      ))}
    </group>
  );
};

const PlotClusters = () => (
  <group>
    {PLOT_CLUSTERS_PDF.map((c) => (
      <PlotCluster
        key={c.id}
        corners={c.corners}
        cols={c.cols}
        rows={c.rows}
        id={c.id}
      />
    ))}
  </group>
);

// =============================================================
// AMENITY GROUP — Building + Floating marker + click area
// =============================================================

const BuildingByType = ({ type, ...props }) => {
  switch (type) {
    case "clubhouse":
      return <Clubhouse {...props} />;
    case "temple":
      return <Temple {...props} />;
    case "playground":
      return <Playground {...props} />;
    case "amphitheater":
      return <Amphitheater {...props} />;
    case "social":
      return <SocialCentre {...props} />;
    case "wellness":
      return <WellnessCentre {...props} />;
    case "entrance":
      return <EntranceGate {...props} />;
    case "park":
      return <ParkScene {...props} />;
    default:
      return null;
  }
};

// Central park scene (Pavilion + trees)
const ParkScene = (props) => (
  <group {...props}>
    {/* Green lawn pad */}
    <mesh
      position={[0, 0.025, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
    >
      <circleGeometry args={[2.3, 32]} />
      <meshStandardMaterial
        color={M.grassBright}
        roughness={0.85}
      />
    </mesh>
    <ParkPavilion position={[0, 0.04, 0]} />
    {/* Trees around the pavilion */}
    {Array.from({ length: 8 }).map((_, i) => {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 16;
      const r = 1.7;
      return (
        <Tree
          key={`tree-${a.toFixed(3)}`}
          position={[Math.cos(a) * r, 0.04, Math.sin(a) * r]}
          scale={0.9 + ((i * 0.13) % 0.5)}
        />
      );
    })}
  </group>
);

const Amenity = ({ hotspot, isActive, someActive, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const [wx, wz] = pdfToWorld(hotspot.pdfPos);
  const markerColor = hotspot.color === "emerald" ? M.emeraldBright : M.gold;

  // Marker height varies by building type
  const markerHeights = {
    clubhouse: 2.6,
    temple: 3.4,
    playground: 2.4,
    amphitheater: 3.2,
    social: 3.0,
    wellness: 3.4,
    entrance: 3.4,
    park: 2.8,
  };
  const markerHeight = markerHeights[hotspot.building] || 2.8;

  return (
    <group
      position={[wx, 0.46, wz]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = "default";
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect && onSelect(hotspot.id);
      }}
    >
      <BuildingByType type={hotspot.building} rotation={[0, hotspot.rot || 0, 0]} />

      {/* Ground glow ring */}
      <mesh
        position={[0, 0.02, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <ringGeometry
          args={[1.8, 2.2, 48]}
        />
        <meshBasicMaterial
          color={markerColor}
          transparent
          opacity={isActive ? 0.55 : hovered ? 0.35 : 0.15}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Floating marker above building */}
      <FloatingMarker
        color={markerColor}
        active={isActive}
        hovered={hovered}
        height={markerHeight}
      />

      {/* Label — hidden when ANY hotspot is active so the side panel
          isn't competing with 3D labels showing through its glass surface.
          The active hotspot's title is already displayed inside the panel,
          so we don't need its in-scene label either. */}
      {!someActive && (
        <Html
          center
          position={[0, markerHeight + 0.6, 0]}
          distanceFactor={10}
          zIndexRange={[5, 0]}
          style={{ pointerEvents: "none" }}
        >
          <div
            className={cn(
              "px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase whitespace-nowrap font-medium backdrop-blur-md transition-all border",
              hovered
                ? "bg-[rgba(212,175,55,0.96)] text-black shadow-[0_0_22px_rgba(212,175,55,0.65)] border-[rgba(212,175,55,0.9)]"
                : "bg-black/65 text-white/90 border-white/15"
            )}
            style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}
          >
            {hotspot.title}
          </div>
        </Html>
      )}
    </group>
  );
};

const Amenities = ({ activeId, onSelect }) => (
  <group>
    {HOTSPOTS.map((h) => (
      <Amenity
        key={h.id}
        hotspot={h}
        isActive={activeId === h.id}
        someActive={Boolean(activeId)}
        onSelect={onSelect}
      />
    ))}
  </group>
);

// =============================================================
// CAMERA RIG
// =============================================================

const CameraRig = ({ orbitRef, focus, target }) => {
  const { camera } = useThree();
  const desiredPos = useRef(new THREE.Vector3(...focus));
  const desiredTgt = useRef(new THREE.Vector3(...target));

  useEffect(() => {
    desiredPos.current.set(...focus);
    desiredTgt.current.set(...target);
  }, [focus, target]);

  useFrame((_, dt) => {
    camera.position.lerp(desiredPos.current, 1 - Math.exp(-dt * 2.4));
    if (orbitRef?.current) {
      const t = orbitRef.current.target;
      t.lerp(desiredTgt.current, 1 - Math.exp(-dt * 2.4));
      orbitRef.current.update();
    }
  });
  return null;
};

// Expose orbit controls zoom externally
const ZoomController = ({ orbitRef, zoomCommandRef }) => {
  useFrame(() => {
    const cmd = zoomCommandRef.current;
    if (cmd) {
      const oc = orbitRef.current;
      if (oc) {
        const dir = new THREE.Vector3()
          .subVectors(oc.object.position, oc.target)
          .normalize();
        const dist = oc.object.position.distanceTo(oc.target);
        const newDist = THREE.MathUtils.clamp(
          dist * (cmd === "in" ? 0.78 : 1.28),
          oc.minDistance,
          oc.maxDistance
        );
        oc.object.position.copy(
          oc.target.clone().add(dir.multiplyScalar(newDist))
        );
        oc.update();
      }
      zoomCommandRef.current = null;
    }
  });
  return null;
};

// =============================================================
// SCENE
// =============================================================

const Scene = ({
  activeId,
  onSelect,
  cameraFocus,
  cameraTarget,
  autoRotate,
  orbitRef,
  zoomCommandRef,
}) => {
  return (
    <>
      {/* Lighting — warm golden hour + soft fill */}
      <ambientLight intensity={0.55} color="#fff0c7" />
      <directionalLight
        position={[20, 28, 16]}
        intensity={1.45}
        color="#ffcc7a"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={80}
        shadow-camera-left={-35}
        shadow-camera-right={35}
        shadow-camera-top={35}
        shadow-camera-bottom={-35}
      />
      <directionalLight
        position={[-14, 10, -12]}
        intensity={0.45}
        color="#1a9a4f"
      />
      <hemisphereLight
        intensity={0.32}
        color="#ffd99b"
        groundColor="#0a0a0a"
      />

      {/* Stars in night sky */}
      <Stars
        radius={140}
        depth={60}
        count={1400}
        factor={2}
        saturation={0}
        fade
        speed={0.4}
      />

      <SitePlate />
      <MountainRange />
      <PlotClusters />
      <RoadNetwork />
      <Amenities activeId={activeId} onSelect={onSelect} />

      <OrbitControls
        ref={orbitRef}
        enableDamping
        dampingFactor={0.1}
        enableZoom
        zoomSpeed={0.95}
        autoRotate={autoRotate}
        autoRotateSpeed={0.42}
        minDistance={6}
        maxDistance={75}
        minPolarAngle={Math.PI / 8}
        maxPolarAngle={Math.PI / 2.05}
        target={cameraTarget}
        makeDefault
      />
      <CameraRig orbitRef={orbitRef} focus={cameraFocus} target={cameraTarget} />
      <ZoomController orbitRef={orbitRef} zoomCommandRef={zoomCommandRef} />
    </>
  );
};

// =============================================================
// HOTSPOT POPUP
// =============================================================

const HotspotPanel = ({ hotspot, onClose, audioRef, isPlaying, togglePlay, imageUrl }) => {
  const audioSrc = audioUrlFor(hotspot.audio);

  return (
    <motion.div
      key={hotspot.id}
      initial={{ opacity: 0, x: -40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -40 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`hotspot-popup-${hotspot.id}`}
      className="pointer-events-auto absolute inset-x-3 bottom-3 lg:inset-y-6 lg:left-6 lg:right-auto lg:bottom-auto lg:top-20 lg:w-[400px] z-20"
    >
      <div className="bg-[#0a0a0a]/96 backdrop-blur-2xl neon-border rounded-[20px] overflow-hidden border max-h-[78vh] flex flex-col">
        {/* Header image / GIF */}
        <div className="relative aspect-[16/9] w-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#1a1410] via-[#0c0c0c] to-[#0a1410]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={hotspot.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full mx-auto bg-[color:var(--sp-gold-soft)] border border-[color:rgba(212,175,55,0.45)] flex items-center justify-center">
                  <Volume2 className="h-7 w-7 text-[color:var(--sp-gold)]" />
                </div>
                <p className="mt-3 text-[10px] tracking-[0.32em] uppercase text-white/55">
                  Visual coming soon
                </p>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
          <button
            type="button"
            onClick={onClose}
            data-testid="hotspot-popup-close"
            aria-label="Close panel"
            className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 border border-white/15 text-white/85 hover:text-[color:var(--sp-gold)] hover:border-[color:rgba(212,175,55,0.55)] transition-colors backdrop-blur-md"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-3 left-4 right-4">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[color:var(--sp-gold)]">
              {hotspot.eyebrow}
            </p>
            <h3 className="font-display text-2xl mt-1 leading-tight text-white">
              {hotspot.title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 md:p-6 overflow-y-auto">
          <p className="text-sm text-white/75 leading-relaxed">{hotspot.description}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {hotspot.badges.map((b) => (
              <span
                key={b}
                className="inline-flex items-center rounded-full border border-white/12 bg-white/4 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-white/80"
              >
                {b}
              </span>
            ))}
          </div>

          {/* Audio narration */}
          <div
            data-testid="hotspot-audio-player"
            className="mt-5 rounded-2xl border border-[color:rgba(212,175,55,0.25)] bg-black/40 px-4 py-3.5"
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                data-testid="hotspot-audio-toggle"
                disabled={!audioSrc}
                aria-label={isPlaying ? "Pause narration" : "Play narration"}
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-full transition-all flex-shrink-0",
                  audioSrc
                    ? "bg-[color:var(--sp-gold)] text-black hover:brightness-110 shadow-[0_0_16px_rgba(212,175,55,0.45)]"
                    : "bg-white/8 text-white/40 cursor-not-allowed"
                )}
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Voice Narration
                </p>
                <div className="mt-1.5 flex items-end gap-[2px] h-5 overflow-hidden">
                  {Array.from({ length: 26 }).map((_, i) => (
                    <span
                      key={`waveform-bar-${i}`}
                      className={cn(
                        "w-[3px] rounded-full",
                        isPlaying
                          ? "bg-[color:var(--sp-gold)] animate-pulse"
                          : "bg-white/22"
                      )}
                      style={{
                        height: `${
                          isPlaying
                            ? 4 + (Math.sin((Date.now() + i * 100) / 220) + 1) * 7
                            : 4 + ((i * 7) % 12)
                        }px`,
                        animationDelay: `${i * 60}ms`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            {audioSrc && (
              <audio
                ref={audioRef}
                src={audioSrc}
                preload="metadata"
              />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// =============================================================
// MAIN COMPONENT
// =============================================================

const ISO_CAMERA = [32, 26, 32];
const ISO_TARGET = [0, 0, 0];

const ProjectMap3D = () => {
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [tourMode, setTourMode] = useState(false);
  const [cameraFocus, setCameraFocus] = useState(ISO_CAMERA);
  const [cameraTarget, setCameraTarget] = useState(ISO_TARGET);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = useRef(null);
  const orbitRef = useRef(null);
  const tourTimerRef = useRef(null);
  const zoomCommandRef = useRef(null);

  const activeHotspot = useMemo(
    () => HOTSPOTS.find((h) => h.id === activeId) || null,
    [activeId]
  );
  const activeImageUrl = useMedia(activeHotspot?.mediaKey);

  // ---- Audio sync ----
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioPlaying(false);
  }, [activeId]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onPlay = () => setAudioPlaying(true);
    const onPause = () => setAudioPlaying(false);
    const onEnded = () => setAudioPlaying(false);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [activeId]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => setAudioPlaying(false));
    }
  };

  const focusHotspot = useCallback((id, withSound = false) => {
    const h = HOTSPOTS.find((x) => x.id === id);
    if (!h) return;
    setActiveId(id);
    setAutoRotate(false);
    const [wx, wz] = pdfToWorld(h.pdfPos);
    // Wider, more cinematic camera offset
    setCameraFocus([wx + 14, 14, wz + 14]);
    setCameraTarget([wx, 1.0, wz]);
    if (withSound) {
      setTimeout(() => {
        if (audioRef.current) audioRef.current.play().catch(() => {});
      }, 400);
    }
  }, []);

  const closePanel = useCallback(() => {
    setActiveId(null);
    setCameraFocus(ISO_CAMERA);
    setCameraTarget(ISO_TARGET);
    if (audioRef.current) audioRef.current.pause();
  }, []);

  // ---- Tour ----
  const startTour = () => {
    setTourMode(true);
    setAutoRotate(false);
    let idx = 0;
    const visitNext = () => {
      if (idx >= HOTSPOTS.length) {
        setTourMode(false);
        closePanel();
        return;
      }
      focusHotspot(HOTSPOTS[idx].id, true);
      idx += 1;
      tourTimerRef.current = setTimeout(visitNext, 10000);
    };
    visitNext();
  };

  const stopTour = () => {
    setTourMode(false);
    if (tourTimerRef.current) {
      clearTimeout(tourTimerRef.current);
      tourTimerRef.current = null;
    }
    if (audioRef.current) audioRef.current.pause();
  };

  useEffect(() => {
    return () => {
      if (tourTimerRef.current) clearTimeout(tourTimerRef.current);
    };
  }, []);

  // ---- Fullscreen ----
  // We support BOTH the native Fullscreen API (Chrome, Firefox, desktop Safari)
  // and a CSS-based "pseudo fullscreen" fallback for iOS Safari which doesn't
  // expose requestFullscreen on arbitrary elements.
  const [pseudoFullscreen, setPseudoFullscreen] = useState(false);
  const supportsNativeFs = useMemo(() => {
    if (typeof document === "undefined") return false;
    const el = document.createElement("div");
    return Boolean(
      el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen
    );
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!supportsNativeFs) {
      // iOS Safari path: CSS pseudo-fullscreen
      setPseudoFullscreen((p) => !p);
      return;
    }
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen;
      if (req) req.call(el).catch(() => {});
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      if (exit) exit.call(document).catch(() => {});
    }
  };

  useEffect(() => {
    const onChange = () =>
      setIsFullscreen(
        Boolean(document.fullscreenElement || document.webkitFullscreenElement)
      );
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Sync pseudo-fullscreen into the same isFullscreen flag the UI listens to,
  // so a single state drives all "isFullscreen" styling.
  useEffect(() => {
    if (!supportsNativeFs) setIsFullscreen(pseudoFullscreen);
  }, [pseudoFullscreen, supportsNativeFs]);

  // Lock body scroll while pseudo-fullscreen is active (mobile UX)
  useEffect(() => {
    if (pseudoFullscreen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [pseudoFullscreen]);

  // Press ESC to exit pseudo-fullscreen on devices that have a keyboard
  useEffect(() => {
    if (!pseudoFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPseudoFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pseudoFullscreen]);

  // ---- Zoom buttons ----
  const zoomIn = () => {
    zoomCommandRef.current = "in";
  };
  const zoomOut = () => {
    zoomCommandRef.current = "out";
  };

  // ---- Minimap dot for current camera target (in PDF coords) ----
  const minimapDot = useMemo(() => {
    const [wx, , wz] = cameraTarget;
    return [wx / SCALE + 50, wz / SCALE + 50];
  }, [cameraTarget]);

  return (
    <div
      ref={containerRef}
      data-testid="project-map-3d"
      className={cn(
        "relative w-full overflow-hidden border bg-gradient-to-br from-[#0c100e] via-[#0a0a0a] to-[#0c0d0f]",
        isFullscreen
          ? "fixed inset-0 z-[9998] h-screen w-screen rounded-none border-none"
          : "rounded-[24px] border-white/10 h-[640px] md:h-[760px]"
      )}
    >
      <Canvas
        shadows
        dpr={[1, 1.8]}
        camera={{ position: ISO_CAMERA, fov: 40 }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
      >
        <Suspense fallback={null}>
          <Scene
            activeId={activeId}
            onSelect={(id) => focusHotspot(id, false)}
            cameraFocus={cameraFocus}
            cameraTarget={cameraTarget}
            autoRotate={autoRotate}
            orbitRef={orbitRef}
            zoomCommandRef={zoomCommandRef}
          />
          <AdaptiveDpr pixelated />
          <AdaptiveEvents />
        </Suspense>
      </Canvas>

      {/* Prominent floating Exit Fullscreen button (always visible above
          everything when in fullscreen). Provides a clear escape on iOS where
          the OS-level fullscreen UI doesn't appear. */}
      {isFullscreen && (
        <button
          type="button"
          onClick={toggleFullscreen}
          data-testid="map-exit-fullscreen"
          aria-label="Exit fullscreen"
          className="absolute right-4 top-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-black/85 backdrop-blur-md border border-[color:rgba(212,175,55,0.55)] text-[color:var(--sp-gold)] shadow-[0_8px_24px_rgba(0,0,0,0.6)] hover:bg-[color:var(--sp-gold)] hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--sp-gold)]"
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Exit fullscreen</span>
        </button>
      )}

      {/* Top bar HUD (hidden when in fullscreen — we have a dedicated exit
          button above; we don't want the small HUD competing with hotspots) */}
      <div className={cn(
        "pointer-events-none absolute top-4 left-4 right-4 flex items-start justify-between gap-3 z-10",
        isFullscreen && "right-20"
      )}>
        <div className="pointer-events-auto glass rounded-full pl-3 pr-5 py-2 border border-white/10 inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-[color:var(--sp-gold)] animate-pulse" />
          <span className="text-[10px] tracking-[0.32em] uppercase text-white/85">
            Silva City — 66 Acre · Interactive
          </span>
        </div>

        <div className="pointer-events-auto flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => (tourMode ? stopTour() : startTour())}
            data-testid="map-tour-toggle"
            className={cn(
              "flex items-center gap-2 rounded-full px-3.5 py-2 backdrop-blur-md border text-[10px] tracking-[0.22em] uppercase transition-colors",
              tourMode
                ? "bg-[color:rgba(4,99,7,0.4)] border-[color:rgba(4,99,7,0.6)] text-[color:#7be4a3]"
                : "bg-[color:var(--sp-gold-soft)] border-[color:rgba(212,175,55,0.55)] text-[color:var(--sp-gold)] hover:brightness-110"
            )}
          >
            {tourMode ? (
              <>
                <StopCircle className="h-3.5 w-3.5" /> Stop Tour
              </>
            ) : (
              <>
                <PlayCircle className="h-3.5 w-3.5" /> Take a Tour
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setAutoRotate((p) => !p)}
            data-testid="map-toggle-autorotate"
            className={cn(
              "hidden md:flex items-center gap-2 rounded-full px-3.5 py-2 backdrop-blur-md border text-[10px] tracking-[0.22em] uppercase transition-colors",
              autoRotate
                ? "bg-[color:var(--sp-gold-soft)] border-[color:rgba(212,175,55,0.55)] text-[color:var(--sp-gold)]"
                : "bg-black/40 border-white/12 text-white/75 hover:border-[color:rgba(212,175,55,0.45)]"
            )}
          >
            <RotateCw
              className={cn(
                "h-3.5 w-3.5",
                autoRotate && "animate-[spin_8s_linear_infinite]"
              )}
            />
            {autoRotate ? "Auto-Rotating" : "Rotate"}
          </button>
          <button
            type="button"
            onClick={toggleFullscreen}
            data-testid="map-fullscreen-toggle"
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            className="flex items-center gap-2 rounded-full px-3.5 py-2 backdrop-blur-md border bg-black/40 border-white/12 text-white/75 hover:border-[color:rgba(212,175,55,0.45)] hover:text-[color:var(--sp-gold)] text-[10px] tracking-[0.22em] uppercase transition-colors"
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="h-3.5 w-3.5" /> Exit
              </>
            ) : (
              <>
                <Maximize2 className="h-3.5 w-3.5" /> Fullscreen
              </>
            )}
          </button>
        </div>
      </div>

      {/* Zoom controls (left side) */}
      <div className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
        <button
          type="button"
          onClick={zoomIn}
          data-testid="map-zoom-in"
          aria-label="Zoom in"
          className="flex h-10 w-10 items-center justify-center rounded-full glass border border-white/12 text-white/85 hover:border-[color:rgba(212,175,55,0.55)] hover:text-[color:var(--sp-gold)] transition-colors backdrop-blur-md"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          data-testid="map-zoom-out"
          aria-label="Zoom out"
          className="flex h-10 w-10 items-center justify-center rounded-full glass border border-white/12 text-white/85 hover:border-[color:rgba(212,175,55,0.55)] hover:text-[color:var(--sp-gold)] transition-colors backdrop-blur-md"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
      </div>

      {/* Legend bottom-left */}
      <div
        className={cn(
          "pointer-events-none absolute left-4 bottom-4 hidden md:block z-10",
          activeId && "lg:hidden"
        )}
      >
        <div className="glass rounded-xl border border-white/10 p-3 space-y-1.5 text-[10px] tracking-[0.22em] uppercase text-white/75">
          <p className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: M.grassBright }}
            />
            Residential Plots
          </p>
          <p className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: M.gold }}
            />
            Premium Amenity
          </p>
          <p className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ background: M.emerald }}
            />
            Civic / Spiritual
          </p>
          <p className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: "#1c3a26" }}
            />
            Forest Hills
          </p>
        </div>
      </div>

      {/* Minimap bottom-right */}
      <div className="pointer-events-none absolute right-4 bottom-4 z-10 hidden md:block">
        <ProjectMinimap
          activeId={activeId}
          onSelect={(id) => focusHotspot(id, false)}
          cameraTarget={activeId ? null : minimapDot}
        />
      </div>

      {/* Bottom hint text */}
      <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 z-10 hidden lg:block">
        <p className="text-[10px] tracking-[0.28em] uppercase text-white/55 text-center px-3">
          Drag to rotate · Scroll to zoom · Tap a building
        </p>
      </div>

      {/* Popup */}
      <AnimatePresence>
        {activeHotspot && (
          <HotspotPanel
            hotspot={activeHotspot}
            onClose={closePanel}
            audioRef={audioRef}
            isPlaying={audioPlaying}
            togglePlay={togglePlay}
            imageUrl={activeImageUrl}
          />
        )}
      </AnimatePresence>

      {/* Initial loading overlay */}
      <Suspense
        fallback={
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]/85 backdrop-blur-sm z-30">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-[color:var(--sp-gold)] mx-auto" />
              <p className="mt-3 text-[11px] tracking-[0.32em] uppercase text-white/65">
                Loading 3D township…
              </p>
            </div>
          </div>
        }
      >
        <></>
      </Suspense>
    </div>
  );
};

export default ProjectMap3D;
